/**
 * OverridePanel — modernized override-flags section of the AbilityTestRig.
 *
 * Four dev-bypass toggle cards (infinite mana / no cooldowns / no GCD / no range gate).
 * Each click mutates the local player object and sends the matching GM chat command.
 */
import React from 'react';
import { Section, type OverrideFlags } from './shared';
import { rigStyles as styles } from './abilityRigStyles';
import { Badge } from '../../primitives';

interface OverrideCardDef {
  key: keyof OverrideFlags;
  label: string;
  icon: string;
  command: string;
  description: string;
}

const OVERRIDES_DEFS: OverrideCardDef[] = [
  {
    key: 'infiniteMana',
    label: 'Infinite Mana',
    icon: '💧',
    command: '/mana',
    description: 'Bypasses mana cost requirements',
  },
  {
    key: 'noCooldowns',
    label: 'No Cooldowns',
    icon: '⏱️',
    command: '/cd',
    description: 'Resets cooldowns immediately on cast',
  },
  {
    key: 'noGCD',
    label: 'No Global Cooldown',
    icon: '⚡',
    command: '/gcd 0',
    description: 'Sets GCD recovery to 0ms',
  },
  {
    key: 'noRangeGate',
    label: 'No Range Gate',
    icon: '🎯',
    command: '/norange',
    description: 'Bypasses target distance checks',
  },
];

export interface OverridePanelProps {
  overrides: OverrideFlags;
  onToggle: (key: keyof OverrideFlags) => void;
}

export const OverridePanel: React.FC<OverridePanelProps> = ({ overrides, onToggle }) => (
  <Section title="Dev Override Flags">
    <div style={styles.overrideGrid}>
      {OVERRIDES_DEFS.map((def) => {
        const active = overrides[def.key];
        return (
          <button
            key={def.key}
            type="button"
            style={{
              ...styles.overrideCard,
              ...(active ? styles.overrideCardActive : {}),
            }}
            onClick={() => onToggle(def.key)}
            title={`${def.description} (Slash command: ${def.command})`}
          >
            <div style={styles.overrideTopRow}>
              <span style={{ fontSize: 16 }}>{def.icon}</span>
              <Badge tone={active ? 'success' : 'neutral'} size="sm">
                {active ? 'ACTIVE' : 'OFF'}
              </Badge>
            </div>
            <div style={styles.overrideTitle}>{def.label}</div>
            <div style={styles.overrideBottomRow}>
              <span style={styles.commandChip}>{def.command}</span>
            </div>
          </button>
        );
      })}
    </div>
  </Section>
);
